import { FC, useState } from 'react';

import { buildSanityImageSrcSet, buildSanityImageUrl } from '@lib/sanity/image';
import { getYoutubeId } from '@lib/youtube';
import type { SanityVideoPlaceholder } from '@lib/sanity/types';

const POSTER_WIDTH = 960;
const POSTER_SRCSET_WIDTHS = [480, 960];
/** The widest slot is the FFH-first hero video (max-w-2xl). */
const POSTER_SIZES = '(min-width: 768px) 672px, 100vw';

/** Minimal-chrome player: our own poster leads, so keep YouTube's to a minimum. */
const PLAYER_PARAMETERS =
  'autoplay=1&rel=0&modestbranding=1&playsinline=1&color=white';

type VideoPlaceholderProps = {
  video?: SanityVideoPlaceholder;
  className?: string;
};

/**
 * The shared "video slot". Until the visitor presses play we render only our
 * own poster and a branded play button (no YouTube iframe), so the player's
 * weight and chrome never touch the initial page load and nothing autoplays.
 * On click we swap in a minimal-chrome YouTube player. Without a URL it stays a
 * static placeholder (useful before content pastes the link).
 */
const VideoPlaceholder: FC<VideoPlaceholderProps> = ({ video, className }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const isPortrait = video?.orientation === 'portrait';
  const youtubeId = getYoutubeId(video?.youtubeUrl);

  const sanityPoster = video?.posterImage
    ? buildSanityImageUrl(video.posterImage)
        .width(POSTER_WIDTH)
        .quality(80)
        .url()
    : null;
  const sanityPosterSrcSet = video?.posterImage
    ? buildSanityImageSrcSet(video.posterImage, POSTER_SRCSET_WIDTHS)
    : undefined;
  // Fall back to the YouTube still if no purpose-shot poster was uploaded.
  const posterUrl =
    sanityPoster ??
    (youtubeId ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg` : null);

  // w-full matters for the play-button case: a <button> shrinks to its content
  // otherwise, collapsing the whole slot.
  const frameClassName = `relative flex w-full items-center justify-center overflow-hidden rounded-2xl bg-landing-ink shadow-[0_18px_40px_-18px_rgba(11,27,43,0.55)] ${
    className ?? ''
  }`;
  const frameStyle = { aspectRatio: isPortrait ? '9 / 16' : '16 / 9' };

  if (isPlaying && youtubeId) {
    return (
      <div className={frameClassName} style={frameStyle}>
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube-nocookie.com/embed/${youtubeId}?${PLAYER_PARAMETERS}`}
          title={video?.label ?? 'Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }

  const posterElement = posterUrl && (
    <img
      src={posterUrl}
      srcSet={sanityPoster ? sanityPosterSrcSet : undefined}
      sizes={sanityPoster ? POSTER_SIZES : undefined}
      alt={video?.label ?? ''}
      decoding="async"
      className="absolute inset-0 h-full w-full object-cover"
    />
  );

  const playBadge = (
    <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white/95 shadow-lg transition-transform group-hover:scale-105 motion-reduce:transition-none">
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="ml-1 h-5 w-5 fill-landing-ink"
      >
        <path d="M5 3 L20 12 L5 21 Z" />
      </svg>
    </span>
  );

  const labelElement = video?.label && (
    <span className="absolute inset-x-0 bottom-3 px-3 text-center text-xs font-medium text-white/70">
      {video.label}
    </span>
  );

  // Static placeholder while there is no URL to play.
  if (!youtubeId) {
    return (
      <div className={frameClassName} style={frameStyle}>
        {posterElement}
        {playBadge}
        {labelElement}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsPlaying(true)}
      aria-label={`Play ${video?.label ?? 'video'}`}
      className={`${frameClassName} group cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600`}
      style={frameStyle}
    >
      {posterElement}

      {/* A darkening veil keeps the play button legible over any poster. */}
      {posterUrl && (
        <span className="absolute inset-0 bg-landing-ink/20 transition-colors group-hover:bg-landing-ink/10 motion-reduce:transition-none" />
      )}

      {playBadge}
      {labelElement}
    </button>
  );
};

export default VideoPlaceholder;
